import type {
  ModelDocument,
  Operation,
  Conflict,
  Diagram,
  Appearance,
  DomainObject,
  NamingRule,
} from "../shared/contracts.js";
import { applyName } from "./transformations.js";
import {
  DomainError,
  clone,
  assertSafeValue,
  syncDocument,
  validateModel,
  uid,
  migrateDiagramRoles,
  diagramAppearanceId,
} from "./model.js";
const equal = (a: unknown, b: unknown) =>
  stableStringify(a) === stableStringify(b);
export function stableStringify(value: unknown): string {
  if (value === undefined) return "undefined";
  if (Array.isArray(value))
    return "[" + value.map(stableStringify).join(",") + "]";
  if (value && typeof value === "object")
    return (
      "{" +
      Object.keys(value)
        .sort()
        .filter((k) => (value as any)[k] !== undefined)
        .map(
          (k) => JSON.stringify(k) + ":" + stableStringify((value as any)[k]),
        )
        .join(",") +
      "}"
    );
  return JSON.stringify(value);
}
const aliases: Record<string, string> = {
  "appearance.add": "appearance.create",
  "diagram.add": "diagram.create",
  "object.add": "object.create",
  "relationship.add": "relationship.create",
  "diagram.connector.upsert": "connector.set",
};
const collectionNames: Record<string, keyof ModelDocument> = {
  object: "objects",
  relationship: "relationships",
  representation: "representations",
  diagram: "diagrams",
  mapping: "mappings",
};
const idNames: Record<string, string> = {
  object: "objectId",
  relationship: "relationshipId",
  representation: "representationId",
  diagram: "diagramId",
  mapping: "mappingId",
};
const writable: Record<string, Set<string>> = {
  object: new Set([
    "name",
    "displayName",
    "description",
    "briefDescription",
    "lifecycle",
    "tags",
    "classifications",
    "sourceIdentities",
    "extensions",
    "type",
    "nullable",
    "defaultValue",
    "generated",
    "logicalType",
    "ordinal",
    "members",
    "keyRole",
    "selectedPrimary",
    "schema",
    "sql",
    "expression",
    "unique",
    "checkExpression",
    "sourceRef",
    "grain",
    "aggregation",
    "documentSchema",
    "physicalKeyStrategy",
  ]),
  relationship: new Set([
    "name",
    "sourceObjectId",
    "targetObjectId",
    "sourceCardinality",
    "targetCardinality",
    "sourceOptional",
    "targetOptional",
    "identifying",
    "description",
    "sourceAttributeIds",
    "targetAttributeIds",
    "onDelete",
    "onUpdate",
    "extensions",
  ]),
  representation: new Set([
    "name",
    "objectIds",
    "targetProfile",
    "sourceRepresentationId",
    "baseline",
    "origin",
  ]),
  diagram: new Set(["name", "role", "parentDiagramId", "roleMigration", "notation", "layoutProfile", "viewportDefault", "appearance", "layoutCheckpoints"]),
  mapping: new Set([
    "sourceRefs",
    "destinationRefs",
    "mappingKind",
    "ruleId",
    "ruleVersion",
    "origin",
    "reviewState",
    "rationale",
  ]),
  model: new Set([
    "name",
    "description",
    "activeRepresentation",
    "modelType",
    "targetProfile",
    "originalConceptualBaseline",
    "namingPolicy",
    "appearance",
  ]),
  metadata: new Set(["extensions", "sourceIdentities", "manifest"]),
  appearance: new Set([
    "position",
    "width",
    "height",
    "pinned",
    "sizeLocked",
    "color",
    "collapsed",
  ]),
};
function patch(
  target: any,
  changes: Record<string, unknown>,
  unset: unknown,
  kind: string,
): { changes: Record<string, unknown>; unset: string[] } {
  if (!changes || typeof changes !== "object" || Array.isArray(changes))
    throw new DomainError("INVALID_CHANGES", "Changes must be a property map.");
  const keys = [
    ...Object.keys(changes),
    ...(Array.isArray(unset) ? unset : []),
  ];
  const old: Record<string, unknown> = {},
    absent: string[] = [];
  for (const key of keys) {
    if (typeof key !== "string" || !writable[kind]?.has(key))
      throw new DomainError(
        "IMMUTABLE_PROPERTY",
        `Cannot modify ${kind}.${String(key)}.`,
      );
    if (Object.prototype.hasOwnProperty.call(target, key))
      old[key] = clone(target[key]);
    else absent.push(key);
  }
  for (const [key, value] of Object.entries(changes))
    target[key] = clone(value);
  if (Array.isArray(unset)) for (const key of unset) delete target[key];
  return { changes: old, unset: absent };
}
function requireDiagram(doc: ModelDocument, id: unknown): Diagram {
  const diagram = doc.diagrams.find((d) => d.diagramId === id);
  if (!diagram)
    throw new DomainError("NOT_FOUND", "Diagram does not exist.", {
      diagramId: id,
    });
  return diagram;
}
function avoidOverlap(diagram: Diagram, appearance: Appearance): Appearance {
  const a = clone(appearance),
    width = a.width ?? 260,
    height = a.height ?? 180;
  let attempts = 0;
  while (
    diagram.appearances.some(
      (b) =>
        a.position.x < b.position.x + (b.width ?? 260) + 24 &&
        a.position.x + width + 24 > b.position.x &&
        a.position.y < b.position.y + (b.height ?? 180) + 24 &&
        a.position.y + height + 24 > b.position.y,
    ) &&
    attempts++ < 1000
  ) {
    a.position.x += width + 48;
    if (attempts % 5 === 0) {
      a.position.x = appearance.position.x;
      a.position.y += height + 64;
    }
  }
  return a;
}
function immutableBaseline(
  doc: ModelDocument,
  op: Operation,
  kind: string,
): void {
  if (["diagram", "appearance", "connector"].includes(kind)) {
    const diagram = doc.diagrams.find((d) => d.diagramId === op.diagramId);
    if (doc.representations.find((r) => r.representationId === diagram?.representationId)?.baseline)
      throw new DomainError("BASELINE_IMMUTABLE", "The preserved conceptual baseline is read-only.");
    return;
  }
  if (!["object", "relationship", "representation"].includes(kind)) return;
  const id = op[idNames[kind]],
    element = (doc[collectionNames[kind]] as any[])?.find(
      (e) => e[idNames[kind]] === id,
    );
  const repId =
    kind === "representation"
      ? id
      : (element?.representationId ?? (op[kind] as any)?.representationId);
  if (doc.representations.find((r) => r.representationId === repId)?.baseline)
    throw new DomainError(
      "BASELINE_IMMUTABLE",
      "The preserved conceptual baseline cannot be edited. Create a derived representation.",
    );
}
const relationshipNameKey = (name: string): string => name.normalize("NFKC").toLowerCase();
/** Validate newly authored captions without rewriting pre-existing imported labels. */
function validateRelationshipText(
  doc: ModelDocument,
  value: ModelDocument["relationships"][number],
  checkName: boolean,
): void {
  if (checkName) {
    if (typeof value.name !== "string" || !value.name.trim() || value.name.length > 512)
      throw new DomainError("INVALID_RELATIONSHIP_NAME", "Relationship name must contain 1 to 512 characters.");
    const collision = doc.relationships.find((other) => other.relationshipId !== value.relationshipId &&
      other.representationId === value.representationId && typeof other.name === "string" &&
      relationshipNameKey(other.name) === relationshipNameKey(value.name));
    if (collision)
      throw new DomainError("RELATIONSHIP_NAME_COLLISION", "A relationship with this name already exists in this representation. Choose another name.",
        { relationshipId: value.relationshipId, existingRelationshipId: collision.relationshipId, name: value.name });
  }
  if (value.description !== undefined && typeof value.description !== "string")
    throw new DomainError("INVALID_RELATIONSHIP_DESCRIPTION", "Relationship description must be text.");
}
export function applyOperations(
  document: ModelDocument,
  operations: Operation[],
  context: { namingRule?: NamingRule; restoreInverse?: boolean } = {},
): { document: ModelDocument; inverse: Operation[]; touched: string[] } {
  if (!Array.isArray(operations) || operations.length > 50000)
    throw new DomainError(
      "INVALID_COMMAND",
      "Operations must be an array of at most 50,000 operations.",
    );
  assertSafeValue(operations);
  const doc = clone(document),
    inverse: Operation[] = [],
    touched = new Set<string>();
  // Schema/presentation repair precedes the user transaction. Treating a legacy
  // missing appearance as an undoable user edit creates a remove/recreate loop
  // and makes later Redo collide with its deterministic identity.
  migrateDiagramRoles(doc);
  const perform = (original: Operation): void => {
    if (!original || typeof original.type !== "string")
      throw new DomainError("INVALID_OPERATION", "Operation requires a type.");
    const op: Operation = {
      ...original,
      type: aliases[original.type] ?? original.type,
    };
    const [kind, action] = op.type.split(".");
    immutableBaseline(document, op, kind);
    if (collectionNames[kind]) {
      const collection = doc[collectionNames[kind]] as any[],
        idName = idNames[kind];
      if (action === "create") {
        const value = op[kind] as any;
        if (!value || typeof value !== "object")
          throw new DomainError(
            "INVALID_OPERATION",
            `${kind}.create requires ${kind}.`,
          );
        if (collection.some((e) => e[idName] === value[idName]))
          throw new DomainError(
            "ALREADY_EXISTS",
            `${kind} identity already exists.`,
            { id: value[idName] },
          );
        if (
          ["object", "relationship"].includes(kind) &&
          document.representations.find(
            (r) => r.representationId === value.representationId,
          )?.baseline
        )
          throw new DomainError(
            "BASELINE_IMMUTABLE",
            "Cannot add to preserved baseline.",
          );
        const created = clone(value);
        if (kind === "representation" && created.origin === "derived" && created.sourceRepresentationId && !context.restoreInverse) {
          const source = doc.representations.find(r => r.representationId === created.sourceRepresentationId);
          if ((source?.kind === "physical" && created.kind === "conceptual") || (source?.kind === "conceptual" && created.kind === "physical"))
            throw new DomainError("INVALID_DERIVATION_TARGET", "Physical and conceptual derivation must proceed through an explicit logical representation.");
          if (source?.kind === created.kind && doc.model.modelType !== "logical-physical")
            throw new DomainError("INVALID_DERIVATION_TARGET", "Same-type derivation is not supported. Choose a different model type.");
        }
        if (kind === "diagram" && ["subject-area", "view"].includes(created.role) && !created.roleMigration) {
          if (typeof created.name !== "string" || !created.name.trim()) throw new DomainError("NAME_REQUIRED", "Give the diagram a name.");
          if (doc.diagrams.some((d) => d.name.trim().normalize("NFKC").toLocaleLowerCase("en-US") === created.name.trim().normalize("NFKC").toLocaleLowerCase("en-US")))
            throw new DomainError("DIAGRAM_NAME_EXISTS", "A diagram with this name already exists in the model.");
          created.name = created.name.trim();
        }
        if (kind === "relationship" && !context.restoreInverse)
          validateRelationshipText(doc, created, true);
        if (
          kind === "object" &&
          context.namingRule &&
          !context.restoreInverse
        ) {
          const finalPolicy =
            (operations
              .filter(
                (o) => o.type === "model.update" && o.changes?.namingPolicy,
              )
              .at(-1)?.changes
              ?.namingPolicy as ModelDocument["model"]["namingPolicy"]) ??
            document.model.namingPolicy;
          if (
            !finalPolicy ||
            finalPolicy.ruleSetId !== context.namingRule.ruleSetId ||
            finalPolicy.version !== context.namingRule.version
          )
            throw new DomainError(
              "NAMING_POLICY_MISMATCH",
              "The supplied rule does not match the assigned immutable policy version.",
            );
          const memberOf = operations
            .map((o) => o.object)
            .filter(
              (o) => o?.kind === "key" && o.members?.includes(created.objectId),
            );
          const semanticRole =
            (created.extensions?.transformation as any)?.semanticRole ??
            created.extensions?.semanticRole;
          const role =
            semanticRole === "surrogate"
              ? "surrogate"
              : memberOf.some(
                    (k) =>
                      k?.keyRole === "business" || k?.keyRole === "candidate",
                  )
                ? "business"
                : ["concept", "entity", "table", "view"].includes(created.kind)
                  ? "table"
                  : ["column", "attribute", "property"].includes(created.kind)
                    ? "column"
                    : "other";
          created.name = applyName(created.name, context.namingRule, role);
          if (created.extensions?.creationDefaultField === "id") created.name = created.name.replace(/id$/i, "ID");
          if (created.kind === "column" && created.extensions?.creationDefaultField) created.name = created.name.replace(/\s+/g, "");
          if (
            !created.name.trim() ||
            created.name.length > context.namingRule.maxLength
          )
            throw new DomainError(
              "NAMING_POLICY_VIOLATION",
              "The generated name is empty or exceeds the assigned policy length.",
              {
                objectId: created.objectId,
                name: created.name,
                maxLength: context.namingRule.maxLength,
              },
            );
        }
        collection.push(created);
        inverse.unshift({
          type: `${kind}.delete`,
          [idName]: value[idName],
          cascade: true,
        });
        touched.add(value[idName]);
        return;
      }
      const id = op[idName],
        index = collection.findIndex((e) => e[idName] === id);
      if (index < 0)
        throw new DomainError("NOT_FOUND", `${kind} does not exist.`, { id });
      immutableBaseline(document, op, kind);
      const value = collection[index];
      if (action === "update") {
        if (kind === "diagram" && typeof op.changes?.name === "string" && op.changes.name !== value.name) {
          const name = op.changes.name.trim();
          if (!name) throw new DomainError("NAME_REQUIRED", "Give the diagram a name.");
          if (doc.diagrams.some((d) => d.diagramId !== id && d.name.trim().normalize("NFKC").toLocaleLowerCase("en-US") === name.normalize("NFKC").toLocaleLowerCase("en-US")))
            throw new DomainError("DIAGRAM_NAME_EXISTS", "A diagram with this name already exists in the model.");
        }
        let changes = op.changes ?? {};
        if (kind === "object" && ["key", "index"].includes(value.kind) && Array.isArray(changes.members)) {
          const engineering = value.extensions?.engineering;
          const suppliedEngineering = (changes.extensions as any)?.engineering;
          if (Array.isArray(engineering?.memberDirections) && !Object.hasOwn(suppliedEngineering ?? {}, "memberDirections")) {
            const directionById = new Map((value.members ?? []).map((member: string, index: number) => [member, engineering.memberDirections[index] ?? ""]));
            changes = {...changes, extensions:{...value.extensions,...(changes.extensions as any ?? {}),engineering:{...engineering,...suppliedEngineering,memberDirections:changes.members.map((member: string)=>directionById.get(member) ?? "")}}};
          }
        }
        if (kind === "object" && value.kind === "column" && changes.type && value.extensions?.engineering?.inferredType === true) {
          const suppliedExtensions = changes.extensions as Record<string, any> | undefined;
          changes = {...changes, extensions:{...value.extensions,...suppliedExtensions,engineering:{...value.extensions.engineering,...suppliedExtensions?.engineering,inferredType:false}}};
        }
        const back = patch(value, changes, op.unset, kind);
        if (kind === "relationship" && !context.restoreInverse)
          validateRelationshipText(doc, value, Object.hasOwn(op.changes ?? {}, "name") || (Array.isArray(op.unset) && op.unset.includes("name")));
        inverse.unshift({ type: op.type, [idName]: id, ...back });
        touched.add(id as string);
        if (kind === "diagram") value.diagramVersion++;
        return;
      }
      if (action === "delete") {
        if (kind === "diagram" && doc.diagrams.some((d) => d.parentDiagramId === id))
          throw new DomainError("DEPENDENTS_EXIST", "Remove or move this subject area's views before deleting it.");
        if (kind === "object") {
          const owned = doc.objects.filter(
            (o) => o.ownerObjectId === id || o.members?.includes(id as string),
          );
          const relations = doc.relationships.filter(
            (r) =>
              r.sourceObjectId === id ||
              r.targetObjectId === id ||
              r.sourceAttributeIds?.includes(id as string) ||
              r.targetAttributeIds?.includes(id as string),
          );
          const maps = doc.mappings.filter((m) =>
            [...m.sourceRefs, ...m.destinationRefs].some(
              (r) => r.modelId === doc.model.modelId && r.objectId === id,
            ),
          );
          if ((owned.length || relations.length || maps.length) && !op.cascade)
            throw new DomainError(
              "DEPENDENTS_EXIST",
              "This object has dependents. Review and explicitly request cascading deletion.",
              {
                objects: owned.map((o) => o.objectId),
                relationships: relations.map((r) => r.relationshipId),
                mappings: maps.map((m) => m.mappingId),
              },
            );
          for (const child of owned)
            if (doc.objects.some((o) => o.objectId === child.objectId))
              perform({
                type: "object.delete",
                objectId: child.objectId,
                cascade: true,
              });
          for (const rel of relations)
            if (
              doc.relationships.some(
                (r) => r.relationshipId === rel.relationshipId,
              )
            )
              perform({
                type: "relationship.delete",
                relationshipId: rel.relationshipId,
              });
          for (const map of maps)
            if (doc.mappings.some((m) => m.mappingId === map.mappingId))
              perform({ type: "mapping.delete", mappingId: map.mappingId });
          for (const diagram of doc.diagrams)
            for (const appearance of [...diagram.appearances])
              if (appearance.objectId === id)
                perform({
                  type: "appearance.delete",
                  diagramId: diagram.diagramId,
                  appearanceId: appearance.appearanceId,
                });
        }
        if (kind === "relationship")
          for (const diagram of doc.diagrams)
            if (diagram.connectors.some((c) => c.relationshipId === id))
              perform({
                type: "connector.remove",
                diagramId: diagram.diagramId,
                relationshipId: id as string,
              });
        if (kind === "representation") {
          if (doc.model.activeRepresentation === id)
            throw new DomainError(
              "ACTIVE_REPRESENTATION",
              "Switch the active representation before deleting it.",
            );
          if (
            doc.objects.some((o) => o.representationId === id) ||
            doc.diagrams.some((d) => d.representationId === id)
          )
            throw new DomainError(
              "DEPENDENTS_EXIST",
              "Remove representation objects and diagrams explicitly first.",
            );
        }
        const newIndex = collection.findIndex((e) => e[idName] === id);
        collection.splice(newIndex, 1);
        inverse.unshift({ type: `${kind}.create`, [kind]: clone(value) });
        touched.add(id as string);
        return;
      }
    }
    if (kind === "metadata" && action === "update") {
      const back = patch(doc, op.changes ?? {}, op.unset, "metadata");
      inverse.unshift({ type: op.type, ...back });
      touched.add(doc.model.modelId);
      return;
    }
    if (kind === "model" && action === "update") {
      const back = patch(doc.model, op.changes ?? {}, op.unset, "model");
      inverse.unshift({ type: op.type, ...back });
      touched.add(doc.model.modelId);
      return;
    }
    if (kind === "appearance") {
      const diagram = requireDiagram(doc, op.diagramId),
        id = op.appearanceId ?? op.appearance?.appearanceId,
        index = diagram.appearances.findIndex((a) => a.appearanceId === id);
      if (action === "create" || (action === "upsert" && index < 0)) {
        if (!op.appearance)
          throw new DomainError("INVALID_OPERATION", "Appearance is required.");
        if (index >= 0)
          throw new DomainError("ALREADY_EXISTS", "Appearance already exists.");
        const appearance = op.preservePosition
          ? clone(op.appearance)
          : avoidOverlap(diagram, op.appearance);
        diagram.appearances.push(appearance);
        inverse.unshift({
          type: "appearance.delete",
          diagramId: op.diagramId,
          appearanceId: appearance.appearanceId,
        });
        touched.add(appearance.appearanceId);
      } else {
        if (index < 0)
          throw new DomainError("NOT_FOUND", "Appearance does not exist.");
        const appearance = diagram.appearances[index];
        if (action === "delete") {
          const deletesObject = (objectId: string): boolean => {
            const object = doc.objects.find((o) => o.objectId === objectId);
            return operations.some((o) => o.type === "object.delete" && o.objectId === objectId) || !!(object?.ownerObjectId && deletesObject(object.ownerObjectId));
          };
          if (diagram.role === "main" && !context.restoreInverse && !deletesObject(appearance.objectId))
            throw new DomainError("MAIN_DIAGRAM_COMPLETE", "Main diagrams show every entity. Create a view or subject area to show a selection.");
          diagram.appearances.splice(index, 1);
          inverse.unshift({
            type: "appearance.create",
            diagramId: op.diagramId,
            appearance: clone(appearance),
            preservePosition: true,
          });
        } else if (action === "update" || action === "upsert") {
          const changes =
            action === "upsert"
              ? Object.fromEntries(
                  Object.entries(op.appearance ?? {}).filter(([k]) =>
                    writable.appearance.has(k),
                  ),
                )
              : (op.changes ?? {});
          // Explicit saved legacy dimensions remain fixed until an authorized unlock.
          // History restoration uses the original inverse and restores size + lock atomically.
          const sizeLocked = appearance.sizeLocked ?? (appearance.width !== undefined || appearance.height !== undefined);
          const dimensionsChanged = ["width", "height"].some((key) =>
            (Object.hasOwn(changes, key) && !equal(changes[key], appearance[key as "width" | "height"])) ||
            (Array.isArray(op.unset) && op.unset.includes(key) && appearance[key as "width" | "height"] !== undefined));
          if (!context.restoreInverse && sizeLocked && changes.sizeLocked !== false && dimensionsChanged)
            throw new DomainError("ENTITY_SIZE_LOCKED", "Unlock this entity's size before changing its dimensions.", { appearanceId: id });
          const back = patch(appearance, changes, op.unset, "appearance");
          inverse.unshift({
            type: "appearance.update",
            diagramId: op.diagramId,
            appearanceId: id,
            ...back,
          });
        } else
          throw new DomainError(
            "UNKNOWN_OPERATION",
            `Unknown operation ${op.type}.`,
          );
        touched.add(id as string);
      }
      diagram.diagramVersion++;
      return;
    }
    if (kind === "connector" && (action === "set" || action === "remove")) {
      const diagram = requireDiagram(doc, op.diagramId),
        value = op.connector as any,
        id = value?.relationshipId ?? op.relationshipId,
        index = diagram.connectors.findIndex((c) => c.relationshipId === id),
        old = diagram.connectors[index];
      if (action === "set") {
        if (!value || !value.relationshipId)
          throw new DomainError(
            "INVALID_OPERATION",
            "Connector relationship is required.",
          );
        if (index >= 0) diagram.connectors[index] = clone(value);
        else diagram.connectors.push(clone(value));
      } else {
        if (index < 0)
          throw new DomainError("NOT_FOUND", "Connector does not exist.");
        diagram.connectors.splice(index, 1);
      }
      inverse.unshift(
        old
          ? {
              type: "connector.set",
              diagramId: op.diagramId,
              connector: clone(old),
            }
          : {
              type: "connector.remove",
              diagramId: op.diagramId,
              relationshipId: id,
            },
      );
      diagram.diagramVersion++;
      touched.add(id);
      return;
    }
    if (kind === "dependency") {
      const dependency = op.dependency as Record<string, unknown>,
        id = op.instanceId ?? dependency?.instanceId,
        index = doc.dependencies.findIndex((d) => d.instanceId === id);
      if (action === "add") {
        if (!dependency || typeof dependency.instanceId !== "string")
          throw new DomainError(
            "INVALID_DEPENDENCY",
            "Dependency requires an instanceId.",
          );
        if (index >= 0)
          throw new DomainError(
            "ALREADY_EXISTS",
            "Package instance already exists.",
          );
        doc.dependencies.push(clone(dependency));
        inverse.unshift({ type: "dependency.remove", instanceId: id });
      } else if (action === "remove") {
        if (index < 0)
          throw new DomainError(
            "NOT_FOUND",
            "Package instance does not exist.",
          );
        const [old] = doc.dependencies.splice(index, 1);
        inverse.unshift({ type: "dependency.add", dependency: old });
      } else
        throw new DomainError(
          "UNKNOWN_OPERATION",
          `Unknown operation ${op.type}.`,
        );
      touched.add(id as string);
      return;
    }
    throw new DomainError("UNKNOWN_OPERATION", `Unknown operation ${op.type}.`);
  };
  for (const op of operations) perform(op);
  if (!context.restoreInverse) {
    for (const key of doc.objects.filter(o=>o.kind === "key" && (o.selectedPrimary || (o.keyRole === "primary" && o.selectedPrimary !== false)))) {
      for (const id of key.members ?? []) {
        const field = doc.objects.find(o=>o.objectId === id);
        if (!field || field.nullable === false) continue;
        if (operations.some(op=>op.type === "object.update" && op.objectId === id && op.changes?.nullable === true))
          throw new DomainError("PRIMARY_KEY_NULLABLE", "Primary-key members must be mandatory / NOT NULL.");
        if (operations.some(op=>(op.type === "object.create" && op.object?.objectId === key.objectId) || (op.type === "object.update" && op.objectId === key.objectId && (op.changes?.members || op.changes?.selectedPrimary || op.changes?.keyRole))))
          perform({type:"object.update",objectId:id,changes:{nullable:false}});
      }
    }
  }
  // Authoring a new object inside a nested view also authors its parent membership.
  // Existing objects still require the explicit parent-add confirmation workflow.
  const originalObjectIds = new Set(document.objects.map((o) => o.objectId));
  for (const diagram of doc.diagrams.filter((d) => d.role === "view" && d.parentDiagramId)) {
    const parent = doc.diagrams.find((d) => d.diagramId === diagram.parentDiagramId);
    if (!parent) continue;
    for (const appearance of diagram.appearances) {
      if (originalObjectIds.has(appearance.objectId) || parent.appearances.some((a) => a.objectId === appearance.objectId)) continue;
      perform({ type: "appearance.create", diagramId: parent.diagramId, appearance: { ...clone(appearance), appearanceId: diagramAppearanceId(parent.diagramId, appearance.objectId) } });
    }
  }
  // Completeness additions use ordinary operations so undo/redo includes their IDs.
  const membershipChanged = operations.some((op) => {
    const type = aliases[op.type] ?? op.type;
    return /^(object|representation|diagram)\.(create|delete)$/.test(type) || (type === "diagram.update" && (Object.hasOwn(op.changes ?? {}, "role") || (Array.isArray(op.unset) && op.unset.includes("role"))));
  });
  if (membershipChanged) {
    const repaired = clone(doc);
    const repairSummary = migrateDiagramRoles(repaired);
    if (repairSummary.classified || repairSummary.created || repairSummary.restored)
      for (const repair of documentOperations(doc, repaired)) perform(repair);
  }
  syncDocument(doc);
  const errors = validateModel(doc).filter((d) => d.severity === "error");
  if (errors.length)
    throw new DomainError(
      "MODEL_INVALID",
      "Changes would produce an invalid model.",
      errors,
    );
  // Undoing the first transformation must not erase the original conceptual record.
  // Keep its immutable objects and view, while compensating the derived changes.
  const newlyFrozen = doc.representations.filter(
    (r) =>
      r.baseline &&
      !document.representations.some(
        (old) => old.representationId === r.representationId,
      ),
  );
  if (newlyFrozen.length) {
    const repIds = new Set(newlyFrozen.map((r) => r.representationId)),
      retained = new Set<string>([
        ...repIds,
        ...doc.objects
          .filter((o) => repIds.has(o.representationId))
          .map((o) => o.objectId),
        ...doc.relationships
          .filter((r) => repIds.has(r.representationId))
          .map((r) => r.relationshipId),
        ...doc.diagrams
          .filter((d) => repIds.has(d.representationId))
          .map((d) => d.diagramId),
      ]);
    for (let i = inverse.length - 1; i >= 0; i--) {
      const op = inverse[i],
        [kind, action] = op.type.split(".");
      if (action === "delete" && retained.has(op[idNames[kind]] as string))
        inverse.splice(i, 1);
      else if (
        op.type === "model.update" &&
        doc.model.originalConceptualBaseline &&
        repIds.has(doc.model.originalConceptualBaseline)
      ) {
        if (op.changes && "originalConceptualBaseline" in op.changes)
          op.changes.originalConceptualBaseline =
            doc.model.originalConceptualBaseline;
        if (Array.isArray(op.unset))
          op.unset = op.unset.filter((k) => k !== "originalConceptualBaseline");
      }
    }
  }
  return { document: doc, inverse, touched: [...touched] };
}
function readTarget(doc: ModelDocument, op: Operation): any {
  const type = aliases[op.type] ?? op.type,
    [kind] = type.split(".");
  if (kind === "model") return doc.model;
  if (kind === "metadata") return doc;
  if (kind === "appearance")
    return doc.diagrams
      .find((d) => d.diagramId === op.diagramId)
      ?.appearances.find(
        (a) =>
          a.appearanceId === (op.appearanceId ?? op.appearance?.appearanceId),
      );
  if (kind === "connector")
    return doc.diagrams
      .find((d) => d.diagramId === op.diagramId)
      ?.connectors.find(
        (c) =>
          c.relationshipId ===
          ((op.connector as any)?.relationshipId ?? op.relationshipId),
      );
  if (kind === "dependency")
    return doc.dependencies.find(
      (d) =>
        d.instanceId === (op.instanceId ?? (op.dependency as any)?.instanceId),
    );
  return collectionNames[kind]
    ? (doc[collectionNames[kind]] as any[]).find(
        (v) =>
          v[idNames[kind]] ===
          (op[idNames[kind]] ?? (op[kind] as any)?.[idNames[kind]]),
      )
    : undefined;
}
export function operationConflicts(
  base: ModelDocument,
  current: ModelDocument,
  operations: Operation[],
): Conflict[] {
  const conflicts: Conflict[] = [];
  // A saved draft can contain a create followed by several ordinary edits to
  // that new object. Such targets do not exist in either accepted snapshot yet;
  // their absence is not a remote deletion. Track only earlier, collision-free
  // creates, with diagram-scoped identities for nested presentation objects.
  const createdEarlier = new Set<string>();
  const targetKey = (kind: string, id: unknown, diagramId?: unknown) => `${kind}:${kind === "appearance" || kind === "connector" ? String(diagramId ?? "") : ""}:${String(id ?? "")}`;
  for (const op of operations) {
    const type = aliases[op.type] ?? op.type,
      [kind, action] = type.split("."),
      before = readTarget(base, op),
      now = readTarget(current, op);
    const id = (op[idNames[kind]] ??
      op.appearanceId ??
      op.appearance?.appearanceId ??
      (op[kind] as any)?.[idNames[kind]] ??
      (kind === "connector" ? (op.connector as any)?.relationshipId ?? op.relationshipId : undefined) ??
      (kind === "dependency" ? op.instanceId ?? (op.dependency as any)?.instanceId : undefined) ??
      before?.[idNames[kind]]) as string | undefined;
    const identity = targetKey(kind, id, op.diagramId);
    const push = (
      field: string,
      old: unknown,
      local: unknown,
      incoming: unknown,
      message: string,
    ) =>
      conflicts.push({
        path: `${kind}.${id ?? ""}${field ? "." + field : ""}`,
        objectId: id,
        original: clone(old),
        local: clone(local),
        incoming: clone(incoming),
        message,
      });
    if (action === "create" || action === "add") {
      const object = op.object,
        relationship = op.relationship;
      if (
        object?.ownerObjectId &&
        base.objects.some((o) => o.objectId === object.ownerObjectId) &&
        !current.objects.some((o) => o.objectId === object.ownerObjectId)
      )
        push(
          "ownerObjectId",
          object.ownerObjectId,
          object.ownerObjectId,
          undefined,
          "The parent entity was deleted before this field was saved.",
        );
      if (relationship)
        for (const end of ["sourceObjectId", "targetObjectId"] as const)
          if (
            base.objects.some((o) => o.objectId === relationship[end]) &&
            !current.objects.some((o) => o.objectId === relationship[end])
          )
            push(
              end,
              relationship[end],
              relationship[end],
              undefined,
              "A relationship endpoint was deleted before this relationship was saved.",
            );
      if (now)
        push(
          "",
          before,
          op[kind] ?? op.dependency,
          now,
          "This stable identity was created by another command.",
        );
      if (!before && !now) {
        createdEarlier.add(identity);
        if (kind === "diagram" && op.diagram) {
          for (const appearance of op.diagram.appearances) createdEarlier.add(targetKey("appearance", appearance.appearanceId, op.diagram.diagramId));
          for (const connector of op.diagram.connectors) createdEarlier.add(targetKey("connector", connector.relationshipId, op.diagram.diagramId));
        }
      }
      continue;
    }
    if (!before && !now && createdEarlier.has(identity)) {
      if (action === "delete" || action === "remove") createdEarlier.delete(identity);
      continue;
    }
    if (kind === "connector" && action === "set" && !before && !now) createdEarlier.add(identity);
    if (action === "update" || action === "upsert") {
      if (!now) {
        if (action === "upsert" && !before) { createdEarlier.add(identity); continue; }
        push(
          "",
          before,
          op.changes ?? op.appearance,
          undefined,
          "The object was deleted by another user.",
        );
        continue;
      }
      const changes =
        op.changes ??
        Object.fromEntries(
          Object.entries(op.appearance ?? {}).filter(([k]) =>
            writable.appearance.has(k),
          ),
        );
      for (const key of [
        ...Object.keys(changes),
        ...(Array.isArray(op.unset) ? (op.unset as string[]) : []),
      ])
        if (
          !equal(before?.[key], now?.[key]) &&
          !equal(changes[key], now?.[key])
        )
          push(
            key,
            before?.[key],
            changes[key],
            now?.[key],
            "Both edits changed the same property.",
          );
    } else if (!equal(before, now)) {
      push(
        "",
        before,
        action === "delete" || action === "remove" ? null : op.connector,
        now,
        "The target changed after this command was prepared.",
      );
    }
    if (kind === "relationship" && action === "delete" && before) {
      const connectors = (d: ModelDocument) => d.diagrams.flatMap((diagram) => diagram.connectors.filter((c) => c.relationshipId === before.relationshipId).map((connector) => ({ diagramId: diagram.diagramId, connector })));
      if (!equal(connectors(base), connectors(current)))
        push("connectors", connectors(base), null, connectors(current), "Connector geometry or style changed before deletion.");
    }
    if (kind === "object" && action === "delete" && before) {
      const ids = new Set([before.objectId]);
      let changed = true;
      while (changed) {
        changed = false;
        for (const o of base.objects)
          if (
            o.ownerObjectId &&
            ids.has(o.ownerObjectId) &&
            !ids.has(o.objectId)
          ) {
            ids.add(o.objectId);
            changed = true;
          }
      }
      const beforeChildren = base.objects.filter(
        (o) =>
          o.objectId !== before.objectId &&
          (ids.has(o.objectId) || o.members?.some((id) => ids.has(id))),
      );
      const nowChildren = current.objects.filter(
        (o) =>
          o.objectId !== before.objectId &&
          (ids.has(o.objectId) ||
            (o.ownerObjectId && ids.has(o.ownerObjectId)) ||
            o.members?.some((id) => ids.has(id))),
      );
      if (!equal(beforeChildren, nowChildren))
        push(
          "dependents",
          beforeChildren,
          null,
          nowChildren,
          "Dependent attributes or keys changed before deletion.",
        );
      const rels = (d: ModelDocument) =>
        d.relationships.filter(
          (r) =>
            ids.has(r.sourceObjectId) ||
            ids.has(r.targetObjectId) ||
            r.sourceAttributeIds?.some((id) => ids.has(id)) ||
            r.targetAttributeIds?.some((id) => ids.has(id)),
        );
      const removedRelationships = new Set(rels(base).map((r) => r.relationshipId));
      const relatedPresentation = (d: ModelDocument) => d.diagrams.flatMap((diagram) => [
        ...diagram.appearances.filter((a) => ids.has(a.objectId)).map((a) => ({ diagramId: diagram.diagramId, appearance: a })),
        ...diagram.connectors.filter((c) => removedRelationships.has(c.relationshipId)).map((c) => ({ diagramId: diagram.diagramId, connector: c })),
      ] as unknown[]);
      if (!equal(relatedPresentation(base), relatedPresentation(current)))
        push("diagramDependents", relatedPresentation(base), null, relatedPresentation(current), "Diagram appearances changed before deletion.");
      const relatedMappings = (d: ModelDocument) => d.mappings.filter((mapping) => [...mapping.sourceRefs, ...mapping.destinationRefs].some((ref) => ref.modelId === d.model.modelId && ids.has(ref.objectId)));
      if (!equal(relatedMappings(base), relatedMappings(current)))
        push("mappings", relatedMappings(base), null, relatedMappings(current), "Mappings changed before deletion.");
      if (!equal(rels(base), rels(current)))
        push(
          "relationships",
          rels(base),
          null,
          rels(current),
          "Relationships changed before deletion.",
        );
    }
  }
  // Different IDs with the same qualified name still represent a semantic collision.
  for (const op of operations.filter(
    (o) => (aliases[o.type] ?? o.type) === "object.create",
  )) {
    const obj = op.object;
    if (!obj) continue;
    const incoming = current.objects.find(
      (o) =>
        o.objectId !== obj.objectId &&
        o.representationId === obj.representationId &&
        o.ownerObjectId === obj.ownerObjectId &&
        o.schema === obj.schema &&
        o.name.toLowerCase() === obj.name.toLowerCase() &&
        o.kind === obj.kind,
    );
    if (incoming && !base.objects.some((o) => o.objectId === incoming.objectId))
      conflicts.push({
        path: `object.${obj.objectId}.name`,
        objectId: obj.objectId,
        original: undefined,
        local: obj,
        incoming,
        message:
          "Another user created an object with the same name in this namespace.",
      });
  }
  for (const op of operations) {
    const type = aliases[op.type] ?? op.type;
    if (type !== "relationship.create" && !(type === "relationship.update" && Object.hasOwn(op.changes ?? {}, "name"))) continue;
    const relationship = type === "relationship.create" ? op.relationship : {
      ...current.relationships.find((value) => value.relationshipId === op.relationshipId),
      ...op.changes,
    };
    if (!relationship || typeof relationship.name !== "string") continue;
    const nameKey = relationshipNameKey(relationship.name);
    const incoming = current.relationships.find((other) =>
      other.relationshipId !== relationship.relationshipId && other.representationId === relationship.representationId &&
      typeof other.name === "string" && relationshipNameKey(other.name) === nameKey);
    if (incoming && !base.relationships.some((old) => old.relationshipId === incoming.relationshipId && old.name === incoming.name))
      conflicts.push({ path: `relationship.${relationship.relationshipId}.name`, objectId: relationship.relationshipId,
        original: undefined, local: relationship.name, incoming: clone(incoming),
        message: "Another accepted relationship now uses this name. Keep your draft and choose an available name." });
  }
  return conflicts;
}
/** Produce ordinary reviewable commands; never overwrites an entire persisted snapshot. */
export function documentOperations(
  before: ModelDocument,
  after: ModelDocument,
): Operation[] {
  if (
    before.format !== after.format ||
    before.formatVersion !== after.formatVersion ||
    before.model.family !== after.model.family
  )
    throw new DomainError(
      "INCOMPATIBLE_MODEL_CONTRACT",
      "Format version and model family cannot be replaced through an ordinary update.",
    );
  if (before.model.modelId !== after.model.modelId)
    throw new DomainError(
      "MODEL_IDENTITY_MISMATCH",
      "Cannot replace a model with a different stable identity.",
    );
  const ops: Operation[] = [];
  const diff = (a: any, b: any, kind: string) => {
    const changes: Record<string, unknown> = {},
      unset: string[] = [];
    for (const key of writable[kind])
      if (!equal(a[key], b[key])) {
        if (b[key] === undefined) unset.push(key);
        else changes[key] = clone(b[key]);
      }
    return { changes, unset };
  };
  for (const kind of [
    "representation",
    "object",
    "relationship",
    "mapping",
  ] as const) {
    const listBefore = before[collectionNames[kind]] as any[],
      listAfter = after[collectionNames[kind]] as any[],
      idName = idNames[kind];
    for (const v of listAfter) {
      const old = listBefore.find((e) => e[idName] === v[idName]);
      if (!old) ops.push({ type: `${kind}.create`, [kind]: clone(v) });
      else {
        const immutable =
          kind === "object"
            ? ["kind", "ownerObjectId", "representationId"]
            : kind === "relationship"
              ? ["representationId"]
              : kind === "representation"
                ? ["kind"]
                : [];
        for (const key of immutable)
          if (!equal(old[key], v[key]))
            throw new DomainError(
              "IMMUTABLE_PROPERTY",
              `Cannot change ${kind}.${key} under an existing identity; create a mapped realization.`,
            );
        const d = diff(old, v, kind);
        if (Object.keys(d.changes).length || d.unset.length)
          ops.push({ type: `${kind}.update`, [idName]: v[idName], ...d });
      }
    }
  }
  for (const d of after.diagrams) {
    const old = before.diagrams.find((x) => x.diagramId === d.diagramId);
    if (!old) {
      ops.push({ type: "diagram.create", diagram: clone(d) });
      continue;
    }
    if (old.representationId !== d.representationId)
      throw new DomainError(
        "IMMUTABLE_PROPERTY",
        "A diagram cannot silently switch representations.",
      );
    const delta = diff(old, d, "diagram");
    if (Object.keys(delta.changes).length || delta.unset.length)
      ops.push({ type: "diagram.update", diagramId: d.diagramId, ...delta });
    for (const a of d.appearances) {
      const prior = old.appearances.find(
        (x) => x.appearanceId === a.appearanceId,
      );
      if (!prior)
        ops.push({
          type: "appearance.create",
          diagramId: d.diagramId,
          appearance: clone(a),
        });
      else {
        const ad = diff(prior, a, "appearance");
        if (Object.keys(ad.changes).length || ad.unset.length)
          ops.push({
            type: "appearance.update",
            diagramId: d.diagramId,
            appearanceId: a.appearanceId,
            ...ad,
          });
      }
    }
    for (const a of old.appearances)
      if (!d.appearances.some((x) => x.appearanceId === a.appearanceId))
        ops.push({
          type: "appearance.delete",
          diagramId: d.diagramId,
          appearanceId: a.appearanceId,
        });
    for (const c of d.connectors)
      if (
        !equal(
          c,
          old.connectors.find((x) => x.relationshipId === c.relationshipId),
        )
      )
        ops.push({
          type: "connector.set",
          diagramId: d.diagramId,
          connector: clone(c),
        });
    for (const c of old.connectors)
      if (!d.connectors.some((x) => x.relationshipId === c.relationshipId))
        ops.push({
          type: "connector.remove",
          diagramId: d.diagramId,
          relationshipId: c.relationshipId,
        });
  }
  const metadataDiff = diff(before, after, "metadata");
  if (Object.keys(metadataDiff.changes).length || metadataDiff.unset.length)
    ops.push({ type: "metadata.update", ...metadataDiff });
  const modelDiff = diff(before.model, after.model, "model");
  if (Object.keys(modelDiff.changes).length || modelDiff.unset.length)
    ops.push({ type: "model.update", ...modelDiff });
  for (const dependency of after.dependencies)
    if (
      !before.dependencies.some((d) => d.instanceId === dependency.instanceId)
    )
      ops.push({ type: "dependency.add", dependency: clone(dependency) });
  for (const kind of [
    "mapping",
    "relationship",
    "object",
    "diagram",
    "representation",
  ] as const) {
    const idName = idNames[kind];
    let removed = (before[collectionNames[kind]] as any[]).filter(
      (v) =>
        !(after[collectionNames[kind]] as any[]).some(
          (e) => e[idName] === v[idName],
        ),
    );
    if (kind === "object") {
      const depth = (o: DomainObject): number => {
        let n = 0,
          parent = o.ownerObjectId;
        const seen = new Set<string>();
        while (parent && !seen.has(parent)) {
          seen.add(parent);
          n++;
          parent = before.objects.find(
            (v) => v.objectId === parent,
          )?.ownerObjectId;
        }
        return n;
      };
      removed.sort(
        (a, b) =>
          (a.kind === "key" || a.kind === "index" || a.kind === "constraint"
            ? -1000
            : 0) -
            (b.kind === "key" || b.kind === "index" || b.kind === "constraint"
              ? -1000
              : 0) || depth(b) - depth(a),
      );
    }
    for (const v of removed)
      ops.push({ type: `${kind}.delete`, [idName]: v[idName], cascade: false });
  }
  for (const dep of before.dependencies)
    if (!after.dependencies.some((d) => d.instanceId === dep.instanceId))
      ops.push({ type: "dependency.remove", instanceId: dep.instanceId });
  return ops;
}
